import React from 'react';
import { useTagStore } from '../../stores/tagStore';
import { useAsyncOperations } from './asyncCommon';
import { useMemoTagQuery, useAllMemoTagsQuery } from '../query/memoTagQuery';

/**
 * Memo Tag集成Hook - 统一处理memo tag相关的数据操作和状态管理
 */

/**
 * Store状态管理hooks - 直接访问store状态
 */

// Store数据hooks
export const useMemoTagStoreData = () => {
  const memoTags = useTagStore((state) => state.memoTags);
  const currentMemoTag = useTagStore((state) => state.currentMemoTag);
  const status = useTagStore((state) => state.status);
  const error = useTagStore((state) => state.error);
  const isLoading = useTagStore((state) => state.isLoading);
  const clear = useTagStore((state) => state.clear);
  
  return {
    memoTags,
    currentMemoTag,
    status,
    error,
    isLoading,
    clear
  };
};

/**
 * 组合管理hooks - 提供高级数据管理功能
 */

// Memo Tag管理Hook - 符合统一结构模式
export const useMemoTagManager = (memoId?: string) => {
  const storeActions = useTagStore.getState();
  const storeData = useMemoTagStoreData();
  const { safeAsyncOperation } = useAsyncOperations();
  
  // 使用query hooks
  const { data: tagData, isLoading: queryLoading } = useMemoTagQuery(memoId || '', {
    enabled: !!memoId
  });

  // 数据同步逻辑：将React Query数据同步到Zustand store
  React.useEffect(() => {
    if (tagData && !queryLoading && memoId) {
      // 只有当store中无数据时才用React Query数据初始化store，避免覆盖乐观更新
      const currentStoreData = useTagStore.getState().currentMemoTag;
      if (!currentStoreData || currentStoreData.memoId !== memoId) {
        storeActions.setCurrentMemoTag(tagData);
        storeActions.setStatus('idle');
      }
    }
  }, [tagData, queryLoading, memoId]);

  // 简化的数据合并
  const finalMemoTagData = storeData.currentMemoTag?.memoId === memoId ? storeData.currentMemoTag : tagData;

  // 智能状态判断
  const isReady = React.useMemo(() => {
    return !storeData.isLoading && !queryLoading && finalMemoTagData !== null;
  }, [storeData.isLoading, queryLoading, finalMemoTagData]);

  const hasError = React.useMemo(() => {
    return !!storeData.error;
  }, [storeData.error]);

  const isSyncing = React.useMemo(() => {
    return storeData.isLoading || queryLoading;
  }, [storeData.isLoading, queryLoading]);

  // 异步操作方法
  const getMemoTag = React.useCallback(async (targetMemoId: string) => {
    const result = await safeAsyncOperation(async () => {
      storeActions.setStatus('loading');
      storeActions.clearError();
      
      // 使用useMemoTagQuery获取数据
      const { data } = useMemoTagQuery(targetMemoId, { enabled: true });
      
      if (data) {
        storeActions.setStatus('idle');
        return data;
      } else {
        storeActions.setStatus('idle');
        storeActions.setError(`未找到memoId为"${targetMemoId}"的标签`);
        return null;
      }
    }, `获取memoId为"${targetMemoId}"的标签失败`);
    
    return result.data || null;
  }, [safeAsyncOperation]);

  // 强制刷新数据
  const forceRefresh = React.useCallback(async (targetMemoId?: string) => {
    const result = await safeAsyncOperation(async () => {
      storeActions.setStatus('loading');
      storeActions.clearError();
      
      // 使用useMemoTagQuery强制刷新数据
      const { data } = useMemoTagQuery(targetMemoId || memoId || '', { enabled: true });
      
      if (data) {
        // 更新store状态
        storeActions.forceRefreshMemoTagByMemoId(data);
        storeActions.setStatus('idle');
        return data;
      } else {
        // 清除对应的缓存
        storeActions.forceRefreshMemoTagByMemoId(null);
        storeActions.setStatus('idle');
        return null;
      }
    }, '强制刷新memo tag数据失败');
    
    return result.data || null;
  }, [safeAsyncOperation, memoId]);

  const getAllMemoTags = React.useCallback(async () => {
    const result = await safeAsyncOperation(async () => {
      storeActions.setStatus('loading');
      
      // 使用useAllMemoTagsQuery获取数据
      const { data } = useAllMemoTagsQuery({ enabled: true });
      
      if (data) {
        storeActions.setStatus('idle');
        return data;
      } else {
        storeActions.setStatus('idle');
        storeActions.setError('获取所有memo标签失败');
        return null;
      }
    }, '获取所有memo标签失败');
    
    return result.data || null;
  }, [safeAsyncOperation, storeActions]);

  return {
    // Store状态
    ...storeData,
    
    // React Query状态
    tagData,
    queryLoading,
    
    // 组合状态
    isReady,
    hasError,
    isSyncing,
    
    // Store操作
    storeActions,
    
    // 异步操作方法
    getMemoTag,
    getAllMemoTags,
    forceRefresh,
    
    // 智能状态判断
    canUse: isReady && !hasError,
    shouldRetry: hasError && !isSyncing,
    isInitializing: isSyncing && !finalMemoTagData
  };
};

/**
 * 向后兼容的别名 - 保持现有代码的兼容性
 */

// 主要标签管理hook别名
export const useTagManager = useMemoTagManager;

// 别名导出以保持向后兼容
export const useMemoTagStoreActions = () => {
  const state = useTagStore.getState();
  // 添加安全保护，确保返回有效对象
  const actions = state || {};
  return actions || {};
};